import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  Divider,
  Grid,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  InputAdornment,
  Snackbar,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  Tabs,
  Tab,
  Fade,
  Avatar,
  useTheme,
  Slide,
  Grow,
  TablePagination,
  InputBase,
  useMediaQuery,
  Menu,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { 
  Close, Add, Delete, Save, 
  Receipt, Search, Visibility, 
  Print, Download, Business, 
  LocationOn, ShoppingBag,
  TrendingUp, AttachMoney, ReceiptLong,
  CheckCircle, CalendarToday, MoreVert,
  Person
} from '@mui/icons-material';
import { collection, addDoc, updateDoc, deleteDoc, doc, getDocs, serverTimestamp } from 'firebase/firestore';
import { db } from '../Firebase/Config';
import { getAuth } from "firebase/auth";
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';
import TrendingDown from '@mui/icons-material/TrendingDown';

// ==================== STYLED COMPONENTS ====================

const StyledDialog = ({ children, open, onClose, title, icon: Icon, maxWidth = "sm", actions }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth={maxWidth}
      fullWidth
      TransitionComponent={Grow}
      TransitionProps={{ timeout: 400 }}
      PaperProps={{
        sx: {
          borderRadius: fullScreen ? 0 : 3,
          overflow: "hidden",
          background: "linear-gradient(135deg, #ffffff 0%, #fafafa 100%)",
          boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25)",
          display: "flex",
          flexDirection: "column",
          maxHeight: fullScreen ? "100%" : "90vh",
        }
      }}
    >
      <Box sx={{ 
        background: "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)",
        px: 3,
        py: 2,
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        flexShrink: 0
      }}>
        {Icon && (
          <Avatar sx={{ bgcolor: "rgba(255,255,255,0.1)", color: "white" }}>
            <Icon />
          </Avatar>
        )}
        <Typography variant="h6" sx={{ color: "white", fontWeight: 600 }}>
          {title}
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{ 
            ml: "auto",
            color: "white",
            "&:hover": { bgcolor: "rgba(255,255,255,0.1)" }
          }}
        >
          <Close />
        </IconButton>
      </Box>
      <DialogContent sx={{ 
        p: 3, 
        overflowY: "auto",
        flex: 1,
        '&::-webkit-scrollbar': {
          width: '8px',
        },
        '&::-webkit-scrollbar-track': {
          background: '#f1f1f1',
          borderRadius: '4px',
        },
        '&::-webkit-scrollbar-thumb': {
          background: '#c1c1c1',
          borderRadius: '4px',
          '&:hover': {
            background: '#a8a8a8',
          },
        },
      }}>
        {children}
      </DialogContent>
      {actions && (
        <Box sx={{ 
          p: 2, 
          borderTop: "1px solid", 
          borderColor: "divider",
          flexShrink: 0,
          bgcolor: "white"
        }}>
          {actions}
        </Box>
      )}
    </Dialog>
  );
};

const StyledSnackbar = ({ open, onClose, message, severity, duration = 4000 }) => {
  const icons = {
    success: <CheckCircle sx={{ fontSize: 20 }} />,
    error: <Error sx={{ fontSize: 20 }} />,
    warning: <Warning sx={{ fontSize: 20 }} />,
    info: <Info sx={{ fontSize: 20 }} />
  };

  const colors = {
    success: "#10b981",
    error: "#ef4444",
    warning: "#f59e0b",
    info: "#3b82f6"
  };

  return (
    <Snackbar
      open={open}
      autoHideDuration={duration}
      onClose={onClose}
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
      TransitionComponent={Slide}
      TransitionProps={{ direction: "left" }}
    >
      <Paper
        elevation={6}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          p: 2,
          minWidth: 320,
          maxWidth: 400,
          borderRadius: 2,
          borderLeft: `4px solid ${colors[severity]}`,
          background: "white",
          boxShadow: "0 10px 40px rgba(0,0,0,0.1)",
        }}
      >
        <Box sx={{ 
          width: 40, 
          height: 40, 
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: `rgba(${severity === "success" ? "16,185,129" : severity === "error" ? "239,68,68" : severity === "warning" ? "245,158,11" : "59,130,246"}, 0.1)`,
          color: colors[severity]
        }}>
          {icons[severity]}
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontWeight: 600, mb: 0.5 }}>
            {severity === "success" ? "Succès" : 
             severity === "error" ? "Erreur" : 
             severity === "warning" ? "Attention" : "Information"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {message}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose}>
          <Close fontSize="small" />
        </IconButton>
      </Paper>
    </Snackbar>
  );
};

const StatCard = ({ title, value, icon: Icon, color, trend, subtitle, onClick }) => {
  return (
    <Card 
      sx={{ 
        borderRadius: 3,
        cursor: onClick ? "pointer" : "default",
        transition: "all 0.3s ease",
        "&:hover": onClick ? {
          transform: "translateY(-4px)",
          boxShadow: "0 12px 24px rgba(0,0,0,0.1)"
        } : {}
      }}
      onClick={onClick}
    >
      <CardContent>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {title}
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700, color }}>
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="caption" color="text.secondary">
                {subtitle}
              </Typography>
            )}
            {trend && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 1 }}>
                {trend > 0 ? (
                  <TrendingUp sx={{ fontSize: 16, color: "#10b981" }} />
                ) : trend < 0 ? (
                  <TrendingDown sx={{ fontSize: 16, color: "#ef4444" }} />
                ) : null}
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: trend > 0 ? "#10b981" : trend < 0 ? "#ef4444" : "text.secondary",
                    fontWeight: 600
                  }}
                >
                  {trend > 0 ? "+" : ""}{trend}%
                </Typography>
              </Box>
            )}
          </Box>
          <Avatar sx={{ bgcolor: `rgba(${color === "#2563eb" ? "37,99,235" : color === "#10b981" ? "16,185,129" : color === "#f59e0b" ? "245,158,11" : "139,92,246"}, 0.1)`, color }}>
            <Icon />
          </Avatar>
        </Box>
      </CardContent>
    </Card>
  );
};

// Error and Warning Icons
const Error = ({ sx }) => <ErrorIcon sx={sx} />;
const Warning = ({ sx }) => <WarningIcon sx={sx} />;
const Info = ({ sx }) => <InfoIcon sx={sx} />;

// ==================== MAIN COMPONENT ====================

const PdfTemplate = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // États principaux
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // États formulaire
  const [showForm, setShowForm] = useState(true);
  const [showInvoiceList, setShowInvoiceList] = useState(true);
  const [isSaved, setIsSaved] = useState(false);
  const [currentInvoiceId, setCurrentInvoiceId] = useState(null);
  const [editingInvoice, setEditingInvoice] = useState(null);
  
  // États filtres
  const [selectedStatus, setSelectedStatus] = useState("all");
  
  // États dialogs
  const [openProductDialog, setOpenProductDialog] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  
  // États données
  const [clientData, setClientData] = useState({
    name: '',
    address: '',
    taxNumber: '',
    invoiceNumber: '',
    date: new Date().toISOString().split('T')[0],
  });
  
  const [product, setProduct] = useState({
    product: '',
    quantity: 1,
    unit: 'pièce',
    unitPrice: '',
    tva: 19, // Ajout du champ TVA pour chaque produit
  });
  
  const [productList, setProductList] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success"
  });

  const restaurantId = localStorage.getItem("restaurantId");

  // ==================== CHARGEMENT DES DONNÉES ====================

  useEffect(() => {
    if (restaurantId) {
      fetchInvoices();
      fetchRestaurants();
    }
  }, [restaurantId]);

  useEffect(() => {
    filterInvoices();
  }, [searchTerm, invoices, selectedStatus]);

  const fetchInvoices = async () => {
    try {
      const restaurantId = localStorage.getItem("restaurantId");
      if (!restaurantId) return;

      const invoicesRef = collection(db, 'myfacture');
      const snapshot = await getDocs(invoicesRef);
      const invoicesData = [];
      
      snapshot.forEach((doc) => {
        const data = doc.data();
        if (data.restaurantId === restaurantId) {
          invoicesData.push({
            id: doc.id,
            ...data,
          });
        }
      });
      
      invoicesData.sort((a, b) => {
        const dateA = a.createdAt?.toDate?.() || a.createdAt || 0;
        const dateB = b.createdAt?.toDate?.() || b.createdAt || 0;
        return dateB - dateA;
      });
      
      setInvoices(invoicesData);
      setFilteredInvoices(invoicesData);
    } catch (error) {
      console.error("Error fetching invoices:", error);
      showSnackbar("Erreur lors du chargement des factures", "error");
    }
  };

  const fetchRestaurants = async () => {
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) return;

      const restaurantsRef = collection(db, 'restaurants');
      const snapshot = await getDocs(restaurantsRef);
      const restaurantData = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        if (data.ownerId === user.uid) {
          restaurantData.push({
            id: doc.id,
            ...data,
          });
        }
      });
      setRestaurants(restaurantData);
      
      if (restaurantData.length > 0 && !localStorage.getItem('restaurantId')) {
        localStorage.setItem('restaurantId', restaurantData[0].id);
      }
    } catch (err) {
      console.error("Error fetching restaurants:", err);
    }
  };

  const filterInvoices = () => {
    let filtered = [...invoices];
    
    if (searchTerm) {
      filtered = filtered.filter(invoice =>
        invoice.invoiceNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        invoice.client?.name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (selectedStatus !== "all") {
      filtered = filtered.filter(invoice => invoice.status === selectedStatus);
    }
    
    setFilteredInvoices(filtered);
    setPage(0);
  };

  // ==================== GESTION DES FACTURES ====================

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleClientChange = (e) => {
    const { name, value } = e.target;
    setClientData(prev => ({ ...prev, [name]: value }));
  };

  const handleProductChange = (e) => {
    const { name, value } = e.target;
    setProduct(prev => ({ ...prev, [name]: value }));
  };

  const addProduct = () => {
    if (!product.product || product.quantity <= 0 || product.unitPrice <= 0) {
      showSnackbar("Veuillez remplir tous les champs du produit", "warning");
      return;
    }
    
    const newProduct = {
      id: Date.now(),
      product: product.product,
      quantity: parseFloat(product.quantity),
      unit: product.unit,
      unitPrice: parseFloat(product.unitPrice),
      tva: parseFloat(product.tva),
      totalHT: parseFloat(product.quantity) * parseFloat(product.unitPrice),
      totalTVA: (parseFloat(product.quantity) * parseFloat(product.unitPrice)) * (parseFloat(product.tva) / 100),
    };
    setProductList(prev => [...prev, newProduct]);
    setProduct({ product: '', quantity: 1, unit: 'pièce', unitPrice: '', tva: 19 });
    showSnackbar("Produit ajouté avec succès", "success");
  };

  const deleteProduct = (index) => {
    const newProductList = [...productList];
    newProductList.splice(index, 1);
    setProductList(newProductList);
    showSnackbar("Produit supprimé", "info");
  };

  const saveToMyFacture = async () => {
    setSaving(true);
    
    try {
      const finalRestaurantId = localStorage.getItem("restaurantId");
      
      if (!finalRestaurantId) {
        showSnackbar("Erreur: Restaurant non identifié", "error");
        setSaving(false);
        return false;
      }

      if (productList.length === 0) {
        showSnackbar("Veuillez ajouter au moins un produit", "warning");
        setSaving(false);
        return false;
      }

      if (!clientData.name || !clientData.invoiceNumber) {
        showSnackbar("Veuillez remplir toutes les informations client", "warning");
        setSaving(false);
        return false;
      }

      const sumHT = productList.reduce((sum, item) => sum + (item.totalHT || 0), 0);
      const sumTVA = productList.reduce((sum, item) => sum + (item.totalTVA || 0), 0);
      const timbre = 1;
      const totalTTC = sumHT + sumTVA + timbre;

      const restaurant = restaurants.length > 0 ? restaurants[0] : null;

      const factureData = {
        invoiceNumber: clientData.invoiceNumber,
        date: clientData.date,
        status: 'saved',
        client: {
          name: clientData.name,
          address: clientData.address,
          taxNumber: clientData.taxNumber,
        },
        restaurant: restaurant ? {
          id: restaurant.id,
          name: restaurant.name,
          address: restaurant.address,
          phone: restaurant.phone,
          matriculeFiscale: restaurant.matriculeFiscale,
        } : null,
        products: productList.map(item => ({
          id: item.id,
          product: item.product,
          quantity: item.quantity,
          unit: item.unit,
          unitPrice: item.unitPrice,
          tva: item.tva,
          totalHT: item.totalHT,
          totalTVA: item.totalTVA,
        })),
        totals: {
          sumHT: sumHT,
          sumTVA: sumTVA,
          timbre: timbre,
          totalTTC: totalTTC
        },
        restaurantId: finalRestaurantId,
        savedAt: serverTimestamp(),
        savedBy: localStorage.getItem("userName") || "Utilisateur",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      let invoiceRef;
      
      if (currentInvoiceId) {
        invoiceRef = doc(db, 'myfacture', currentInvoiceId);
        await updateDoc(invoiceRef, {
          ...factureData,
          updatedAt: serverTimestamp(),
        });
        showSnackbar("Facture mise à jour avec succès", "success");
      } else {
        const myfactureRef = collection(db, 'myfacture');
        const docRef = await addDoc(myfactureRef, factureData);
        setCurrentInvoiceId(docRef.id);
        showSnackbar("Facture enregistrée avec succès", "success");
      }
      
      setIsSaved(true);
      await fetchInvoices();
      return true;
    } catch (error) {
      console.error("Error saving invoice:", error);
      showSnackbar(`Erreur lors de l'enregistrement: ${error.message}`, "error");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const deleteInvoice = async () => {
    if (!currentInvoiceId) return;
    
    setConfirmAction({
      title: "Supprimer la facture",
      message: "Voulez-vous vraiment supprimer cette facture ? Cette action est irréversible.",
      onConfirm: async () => {
        try {
          const invoiceRef = doc(db, 'myfacture', currentInvoiceId);
          await deleteDoc(invoiceRef);
          setCurrentInvoiceId(null);
          setIsSaved(false);
          showSnackbar("Facture supprimée avec succès", "success");
          
          setShowInvoiceList(true);
          setShowForm(true);
          setProductList([]);
          setClientData({
            name: '',
            address: '',
            taxNumber: '',
            invoiceNumber: '',
            date: new Date().toISOString().split('T')[0],
          });
          await fetchInvoices();
          setShowConfirmDialog(false);
        } catch (error) {
          showSnackbar("Erreur lors de la suppression", "error");
        }
      }
    });
    setShowConfirmDialog(true);
  };

  const handleNewInvoice = () => {
    setEditingInvoice(null);
    setCurrentInvoiceId(null);
    setIsSaved(false);
    setProductList([]);
    setClientData({
      name: '',
      address: '',
      taxNumber: '',
      invoiceNumber: '',
      date: new Date().toISOString().split('T')[0],
    });
    setShowInvoiceList(false);
    setShowForm(true);
  };

  const handleEditInvoice = (invoice) => {
    setEditingInvoice(invoice);
    setCurrentInvoiceId(invoice.id);
    setIsSaved(true);
    
    setClientData({
      name: invoice.client?.name || '',
      address: invoice.client?.address || '',
      taxNumber: invoice.client?.taxNumber || '',
      invoiceNumber: invoice.invoiceNumber || '',
      date: invoice.date || new Date().toISOString().split('T')[0],
    });
    
    setProductList(invoice.products?.map(p => ({
      ...p,
      totalHT: p.quantity * p.unitPrice,
      totalTVA: (p.quantity * p.unitPrice) * (p.tva / 100)
    })) || []);
    
    setShowInvoiceList(false);
    setShowForm(false);
  };

  const handleSubmitForm = () => {
    setShowForm(false);
  };

  const handleBackToList = () => {
    setShowInvoiceList(true);
    setShowForm(true);
  };

  // ==================== EXPORT ====================

  const exportToExcel = (data, filename) => {
    try {
      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Factures');
      
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      
      saveAs(blob, filename);
      showSnackbar("Export Excel réussi", "success");
    } catch (error) {
      showSnackbar("Erreur lors de l'export", "error");
    }
  };

  const exportInvoices = () => {
    const data = filteredInvoices.map(inv => ({
      'N° Facture': inv.invoiceNumber || '',
      'Client': inv.client?.name || '',
      'Date': formatDate(inv.date),
      'Statut': inv.status === 'saved' ? 'Enregistrée' : 'Imprimée',
      'Total HT': formatNumber(inv.totals?.sumHT || 0),
      'Total TVA': formatNumber(inv.totals?.sumTVA || 0),
      'Total TTC': formatNumber(inv.totals?.totalTTC || 0),
      'Créé le': formatDateTime(inv.createdAt)
    }));
    
    exportToExcel(data, `factures_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  // ==================== FORMATAGE ====================

  const formatDate = (dateString) => {
    if (!dateString) return 'Date non définie';
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return 'Date invalide';
      }
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: 'long',
        year: 'numeric'
      });
    } catch (error) {
      console.error("Erreur de formatage de date:", error);
      return 'Date invalide';
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '';
    try {
      let date;
      if (dateString.toDate && typeof dateString.toDate === 'function') {
        date = dateString.toDate();
      } else {
        date = new Date(dateString);
      }
      if (isNaN(date.getTime())) return '';
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return '';
    }
  };

  const formatNumber = (num) => {
    if (num === undefined || num === null) return '0,000';
    return num.toLocaleString('fr-FR', { minimumFractionDigits: 3, maximumFractionDigits: 3 });
  };

  // ==================== STATISTIQUES ====================

  const getInvoiceStats = () => {
    const total = invoices.length;
    const totalAmount = invoices.reduce((sum, inv) => sum + (inv.totals?.totalTTC || 0), 0);
    const averageAmount = total ? totalAmount / total : 0;
    const thisMonth = invoices.filter(inv => {
      const invDate = new Date(inv.date);
      const now = new Date();
      return invDate.getMonth() === now.getMonth() && invDate.getFullYear() === now.getFullYear();
    }).length;
    
    return { total, totalAmount, averageAmount, thisMonth };
  };

  const stats = getInvoiceStats();

  // ==================== PAGINATION ====================

  const paginatedList = filteredInvoices.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // ==================== RENDU ====================

  return (
    <Box sx={{ 
      minHeight: "100vh",
      bgcolor: "#f3f4f6",
    }}>
      {/* Header principal */}
      <Paper 
        sx={{ 
          borderRadius: 0,
          bgcolor: "white",
          borderBottom: "1px solid",
          borderColor: "divider",
          position: "sticky",
          top: 0,
          zIndex: 10
        }}
      >
        <Box sx={{ p: 2, maxWidth: 1400, margin: "auto", width: "100%" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
            <Avatar sx={{ bgcolor: "#2563eb", width: 48, height: 48 }}>
              <ReceiptLong />
            </Avatar>
            
            <Box sx={{ flex: 1 }}>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                Gestion des Factures
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {invoices.length} factures · {formatNumber(stats.totalAmount)} TND total
              </Typography>
            </Box>

            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleNewInvoice}
                sx={{ borderRadius: 2 }}
              >
                Nouvelle facture
              </Button>
              
              <IconButton 
                onClick={(e) => setShowExportMenu(e.currentTarget)}
                sx={{ bgcolor: "rgba(0,0,0,0.04)" }}
              >
                <MoreVert />
              </IconButton>
              
              <Menu
                anchorEl={showExportMenu}
                open={Boolean(showExportMenu)}
                onClose={() => setShowExportMenu(null)}
              >
                <MenuItem onClick={() => {
                  exportInvoices();
                  setShowExportMenu(null);
                }}>
                  <ListItemIcon><Download fontSize="small" /></ListItemIcon>
                  <ListItemText>Exporter les factures</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => {
                  window.print();
                  setShowExportMenu(null);
                }}>
                  <ListItemIcon><Print fontSize="small" /></ListItemIcon>
                  <ListItemText>Imprimer la liste</ListItemText>
                </MenuItem>
              </Menu>
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Contenu principal */}
      <Box sx={{ p: 3, maxWidth: 1400, margin: "auto" }}>
        <Fade in>
          <Box>
            {/* Stats Cards */}
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr 1fr" }, gap: 2, mb: 3 }}>
              <StatCard
                title="Total factures"
                value={stats.total}
                icon={ReceiptLong}
                color="#3b82f6"
              />
              <StatCard
                title="Montant total"
                value={`${formatNumber(stats.totalAmount)} TND`}
                icon={AttachMoney}
                color="#10b981"
              />
              <StatCard
                title="Moyenne par facture"
                value={`${formatNumber(stats.averageAmount)} TND`}
                icon={TrendingUp}
                color="#f59e0b"
              />
              <StatCard
                title="Ce mois-ci"
                value={stats.thisMonth}
                icon={CalendarToday}
                color="#8b5cf6"
              />
            </Box>

            {/* Filtres */}
            <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={6}>
                  <Paper
                    component="form"
                    sx={{
                      p: "2px 4px",
                      display: "flex",
                      alignItems: "center",
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 2
                    }}
                  >
                    <IconButton sx={{ p: "10px" }}>
                      <Search />
                    </IconButton>
                    <InputBase
                      sx={{ ml: 1, flex: 1 }}
                      placeholder="Rechercher une facture..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    {searchTerm && (
                      <IconButton sx={{ p: "10px" }} onClick={() => setSearchTerm("")}>
                        <Close />
                      </IconButton>
                    )}
                  </Paper>
                </Grid>
                
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Statut</InputLabel>
                    <Select
                      value={selectedStatus}
                      onChange={(e) => setSelectedStatus(e.target.value)}
                      label="Statut"
                    >
                      <MenuItem value="all">Tous</MenuItem>
                      <MenuItem value="saved">Enregistrées</MenuItem>
                      <MenuItem value="printed">Imprimées</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} md={3}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => {
                      setSelectedStatus("all");
                      setSearchTerm("");
                    }}
                  >
                    Réinitialiser les filtres
                  </Button>
                </Grid>
              </Grid>
            </Paper>

            {/* Tableau des factures */}
            <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: "#f8fafc" }}>
                    <TableCell>N° Facture</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Client</TableCell>
                    <TableCell align="right">Montant TTC</TableCell>
                    <TableCell>Statut</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedList.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                        <ReceiptLong sx={{ fontSize: 50, color: "#ccc", mb: 2 }} />
                        <Typography color="text.secondary">
                          Aucune facture trouvée
                        </Typography>
                        <Button
                          variant="contained"
                          startIcon={<Add />}
                          onClick={handleNewInvoice}
                          sx={{ mt: 2 }}
                        >
                          Nouvelle facture
                        </Button>
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedList.map((invoice) => (
                      <TableRow key={invoice.id} hover>
                        <TableCell>
                          <Typography sx={{ fontWeight: 500 }}>
                            {invoice.invoiceNumber}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatDateTime(invoice.createdAt)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {formatDate(invoice.date)}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Avatar sx={{ width: 32, height: 32, bgcolor: "rgba(37,99,235,0.1)" }}>
                              <Person sx={{ fontSize: 16, color: "#2563eb" }} />
                            </Avatar>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {invoice.client?.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {invoice.client?.address}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Typography sx={{ fontWeight: 600, color: "#2563eb" }}>
                            {formatNumber(invoice.totals?.totalTTC)} TND
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={invoice.status === 'saved' ? 'Enregistrée' : 'Imprimée'}
                            size="small"
                            sx={{
                              bgcolor: invoice.status === 'saved' ? 'rgba(59,130,246,0.1)' : 'rgba(16,185,129,0.1)',
                              color: invoice.status === 'saved' ? '#3b82f6' : '#10b981',
                              fontWeight: 600
                            }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            onClick={() => handleEditInvoice(invoice)}
                          >
                            <Visibility />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              
              <TablePagination
                rowsPerPageOptions={[10, 25, 50]}
                component="div"
                count={filteredInvoices.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(e, newPage) => setPage(newPage)}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                labelRowsPerPage="Lignes par page"
              />
            </TableContainer>
          </Box>
        </Fade>
      </Box>

      {/* Client Form Dialog */}
      <StyledDialog
        open={showInvoiceList ? false : showForm}
        onClose={handleBackToList}
        title={currentInvoiceId ? "Modifier la facture" : "Nouvelle facture"}
        icon={ReceiptLong}
        maxWidth="md"
        actions={
          <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
            <Button onClick={handleBackToList}>
              Annuler
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmitForm}
              startIcon={<Save />}
            >
              Continuer
            </Button>
          </Box>
        }
      >
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              label="Nom du Client"
              fullWidth
              name="name"
              value={clientData.name}
              onChange={handleClientChange}
              InputProps={{
                startAdornment: <InputAdornment position="start"><Person /></InputAdornment>,
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Numéro de Facture"
              fullWidth
              name="invoiceNumber"
              value={clientData.invoiceNumber}
              onChange={handleClientChange}
              InputProps={{
                startAdornment: <InputAdornment position="start"><Receipt /></InputAdornment>,
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Adresse"
              fullWidth
              name="address"
              value={clientData.address}
              onChange={handleClientChange}
              multiline
              rows={2}
              InputProps={{
                startAdornment: <InputAdornment position="start"><LocationOn /></InputAdornment>,
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Matricule Fiscale"
              fullWidth
              name="taxNumber"
              value={clientData.taxNumber}
              onChange={handleClientChange}
              InputProps={{
                startAdornment: <InputAdornment position="start"><Business /></InputAdornment>,
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Date"
              type="date"
              fullWidth
              name="date"
              value={clientData.date}
              onChange={handleClientChange}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><CalendarToday /></InputAdornment>,
              }}
            />
          </Grid>
        </Grid>
      </StyledDialog>

      {/* Invoice Preview Dialog - Now Scrollable */}
      <StyledDialog
        open={!showInvoiceList && !showForm}
        onClose={handleBackToList}
        title={`Facture ${clientData.invoiceNumber}`}
        icon={ReceiptLong}
        maxWidth="lg"
        actions={
          <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end", flexWrap: "wrap" }}>
            <Button onClick={handleBackToList}>
              Retour
            </Button>
            {!isSaved && (
              <Button
                variant="contained"
                onClick={saveToMyFacture}
                startIcon={saving ? <CircularProgress size={20} /> : <Save />}
                disabled={saving}
              >
                {saving ? "Enregistrement..." : "Enregistrer"}
              </Button>
            )}
            {isSaved && (
              <Button
                variant="outlined"
                color="error"
                onClick={deleteInvoice}
                startIcon={<Delete />}
              >
                Supprimer
              </Button>
            )}
            <Button
              variant="outlined"
              startIcon={<Print />}
              onClick={() => window.print()}
            >
              Imprimer
            </Button>
          </Box>
        }
      >
        <Box>
          {/* En-tête */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={6}>
              {restaurants.map((restaurant) => (
                <Box key={restaurant.id}>
                  <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                    {restaurant.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {restaurant.address}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Tél: {restaurant.phone}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    MF: {restaurant.matriculeFiscale}
                  </Typography>
                </Box>
              ))}
            </Grid>
            <Grid item xs={12} md={6} sx={{ textAlign: { xs: "left", md: "right" } }}>
              <Typography variant="h4" sx={{ fontWeight: 700, color: "#2563eb" }}>
                FACTURE
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 600, mt: 2 }}>
                N° {clientData.invoiceNumber}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Date: {clientData.date}
              </Typography>
            </Grid>
          </Grid>

          {/* Infos client */}
          <Paper sx={{ p: 3, mb: 4, bgcolor: "#f8fafc", borderRadius: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
              Client
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" color="text.secondary">Nom</Typography>
                <Typography sx={{ fontWeight: 500 }}>{clientData.name}</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" color="text.secondary">Adresse</Typography>
                <Typography>{clientData.address}</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" color="text.secondary">Matricule Fiscal</Typography>
                <Typography>{clientData.taxNumber}</Typography>
              </Grid>
            </Grid>
          </Paper>

          {/* Tableau des produits */}
          <TableContainer component={Paper} sx={{ mb: 4, borderRadius: 2, overflowX: "auto" }}>
            <Table sx={{ minWidth: 700 }}>
              <TableHead>
                <TableRow sx={{ bgcolor: "#f1f5f9" }}>
                  <TableCell>Produit</TableCell>
                  <TableCell align="center">Quantité</TableCell>
                  <TableCell align="center">Unité</TableCell>
                  <TableCell align="right">Prix unitaire</TableCell>
                  <TableCell align="center">TVA</TableCell>
                  <TableCell align="right">Total HT</TableCell>
                  <TableCell align="right">Total TVA</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {productList.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      <ShoppingBag sx={{ fontSize: 40, color: "#ccc", mb: 1 }} />
                      <Typography color="text.secondary">
                        Aucun produit. Ajoutez des produits à la facture.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  productList.map((item, index) => (
                    <TableRow key={item.id} hover>
                      <TableCell>{item.product}</TableCell>
                      <TableCell align="center">{item.quantity}</TableCell>
                      <TableCell align="center">{item.unit}</TableCell>
                      <TableCell align="right">{formatNumber(item.unitPrice)} TND</TableCell>
                      <TableCell align="center">{item.tva}%</TableCell>
                      <TableCell align="right">
                        <Typography sx={{ fontWeight: 500 }}>
                          {formatNumber(item.totalHT)} TND
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography sx={{ fontWeight: 500, color: "#f59e0b" }}>
                          {formatNumber(item.totalTVA)} TND
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <IconButton size="small" onClick={() => deleteProduct(index)}>
                          <Delete fontSize="small" color="error" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Bouton ajout produit */}
          <Box sx={{ display: "flex", justifyContent: "center", mb: 4 }}>
            <Button
              variant="outlined"
              startIcon={<Add />}
              onClick={() => setOpenProductDialog(true)}
            >
              Ajouter un produit
            </Button>
          </Box>

          {/* Totaux */}
          {productList.length > 0 && (() => {
            const sumHT = productList.reduce((s, i) => s + i.totalHT, 0);
            const sumTVA = productList.reduce((s, i) => s + i.totalTVA, 0);
            const timbre = 1;
            const totalTTC = sumHT + sumTVA + timbre;
            
            return (
              <Grid container justifyContent="flex-end">
                <Grid item xs={12} md={5}>
                  <Paper sx={{ p: 2, bgcolor: "#f8fafc", borderRadius: 2 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                      <Typography>Total HT</Typography>
                      <Typography sx={{ fontWeight: 500 }}>{formatNumber(sumHT)} TND</Typography>
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                      <Typography>Total TVA</Typography>
                      <Typography sx={{ color: "#f59e0b" }}>{formatNumber(sumTVA)} TND</Typography>
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                      <Typography>Timbre fiscal</Typography>
                      <Typography>{formatNumber(timbre)} TND</Typography>
                    </Box>
                    <Divider sx={{ my: 1 }} />
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                      <Typography variant="h6">Total TTC</Typography>
                      <Typography variant="h6" sx={{ color: "#2563eb", fontWeight: 700 }}>
                        {formatNumber(totalTTC)} TND
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            );
          })()}
        </Box>
      </StyledDialog>

      {/* Product Dialog - Now Scrollable */}
      <StyledDialog
        open={openProductDialog}
        onClose={() => setOpenProductDialog(false)}
        title="Ajouter un produit"
        icon={ShoppingBag}
        maxWidth="sm"
        actions={
          <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
            <Button onClick={() => setOpenProductDialog(false)}>
              Annuler
            </Button>
            <Button
              variant="contained"
              onClick={addProduct}
              startIcon={<Add />}
            >
              Ajouter
            </Button>
          </Box>
        }
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              label="Nom du produit"
              fullWidth
              name="product"
              value={product.product}
              onChange={handleProductChange}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Quantité"
              type="number"
              fullWidth
              name="quantity"
              value={product.quantity}
              onChange={handleProductChange}
              inputProps={{ min: 1 }}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Unité"
              fullWidth
              name="unit"
              value={product.unit}
              onChange={handleProductChange}
              select
            >
              {['pièce', 'kg', 'm', 'l', 'heure'].map((unit) => (
                <MenuItem key={unit} value={unit}>{unit}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Prix unitaire HT"
              type="number"
              fullWidth
              name="unitPrice"
              value={product.unitPrice}
              onChange={handleProductChange}
              inputProps={{ step: 0.01, min: 0 }}
              InputProps={{
                startAdornment: <InputAdornment position="start">TND</InputAdornment>,
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>TVA</InputLabel>
              <Select
                label="TVA"
                name="tva"
                value={product.tva}
                onChange={handleProductChange}
              >
                <MenuItem value={19}>19%</MenuItem>
                <MenuItem value={7}>7%</MenuItem>
                <MenuItem value={0}>0%</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </StyledDialog>

      {/* Dialog Confirmation */}
      <Dialog
        open={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            {confirmAction?.title}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {confirmAction?.message}
          </Typography>
          <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
            <Button onClick={() => setShowConfirmDialog(false)}>
              Annuler
            </Button>
            <Button
              variant="contained"
              color={confirmAction?.title?.includes("Supprimer") ? "error" : "primary"}
              onClick={() => {
                confirmAction?.onConfirm();
                setShowConfirmDialog(false);
              }}
            >
              Confirmer
            </Button>
          </Box>
        </Box>
      </Dialog>

      {/* Snackbar */}
      <StyledSnackbar
        open={snackbar.open}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
        severity={snackbar.severity}
      />
    </Box>
  );
};

export default PdfTemplate;